import { CreateHearingLinkData } from 'types/HearingLink';
import { observable, action } from 'mobx';
import { routes, hearingLinkStatus } from 'utility/constants';
import History from 'utility/history';
import i18n from 'i18n';
import {
  CombinedPDFStatus,
  HearingLink,
  HearingLinkItem,
  LinkInfoOption,
  ProductListParams,
  ViewingLog,
  ViewSettingOption,
} from 'types/HearingLink';
import { HearingItem } from 'types/HearingSet';
import { SharingLinkResult } from 'types/HearingLink';
import { ProductItem } from 'types/Product';
import { MessageProps } from 'types/App';
import { ListMeta } from 'types/common';
import { Client } from 'types/Client';
import { PlaylistItem } from 'types/Playlist';
import { RootStore } from 'stores/index';
import { defaultHearingLink, defaultListMeta } from 'stores/HearingLinkStore/constants';
import HearingSetApi from 'services/HearingSetApi';
import ProductContentApi from 'services/ProductContentApi';
import HearingLinkApi from 'services/HearingLinkApi';
import ClientApi from 'services/ClientApi';
import { searchByTitle, trimSpace } from 'utility/helpers';
import UserAnswerApi from 'services/UserAnswerApi';
import PlaylistApi from 'services/Admin/PlaylistApi';

class HearingLinkStore {
  private readonly hearingSetApi: HearingSetApi;
  private readonly productContentApi: ProductContentApi;
  private readonly hearingLinkApi: HearingLinkApi;
  private readonly userAnswerApi: UserAnswerApi;
  private readonly clientApi: ClientApi;
  private readonly playlistApi: PlaylistApi;

  @observable public loading = false;
  @observable public submitting = false;
  @observable public loadingHearingList = false;
  @observable public loadingPlaylistList = false;
  @observable public loadingFileList = false;
  @observable public productContentList: ProductItem[] = [];
  @observable public error: any = {};
  @observable public hearingLinkList: HearingLinkItem[] = [];
  @observable public linkListMeta: ListMeta = { ...defaultListMeta };
  @observable public hearingList: HearingItem[] = [];
  @observable public hearingListConst: HearingItem[] = [];
  @observable public clientList: Client[] = [];
  @observable public hearingLinkDetail: HearingLink = { ...defaultHearingLink };
  @observable public rootStore: RootStore;
  @observable public hearingLinkArchives: HearingLinkItem[] = [];
  @observable public combinedPDFStatus: CombinedPDFStatus | undefined = 'pending';
  @observable public regeneratingPDF = false;
  @observable public isEditingHearingDetail = false;
  @observable public hearingLinkResults: SharingLinkResult[] = [];
  @observable public linkViewLogs: ViewingLog[] = [];
  @observable public playlistList: PlaylistItem[] = [];

  constructor({
    rootStore,
    hearingSetApi,
    productContentApi,
    hearingLinkApi,
    userAnswerApi,
    clientApi,
    playlistApi,
  }: {
    rootStore: RootStore;
    hearingSetApi: HearingSetApi;
    productContentApi: ProductContentApi;
    hearingLinkApi: HearingLinkApi;
    userAnswerApi: UserAnswerApi;
    clientApi: ClientApi;
    playlistApi: PlaylistApi;
  }) {
    this.rootStore = rootStore;
    this.hearingSetApi = hearingSetApi;
    this.productContentApi = productContentApi;
    this.hearingLinkApi = hearingLinkApi;
    this.userAnswerApi = userAnswerApi;
    this.clientApi = clientApi;
    this.playlistApi = playlistApi;
  }

  @action.bound
  public setRegeneratingPDF(regeneratingPDF: boolean) {
    this.regeneratingPDF = regeneratingPDF;
  }

  @action.bound
  public setIsEditingHearingDetail(isEditingHearingDetail: boolean) {
    this.isEditingHearingDetail = isEditingHearingDetail;
  }

  @action.bound
  public handleChangeNoteResult(sessionId: string, answerId: number, note: string) {
    this.hearingLinkResults = this.hearingLinkResults.map(session =>
      session.id === sessionId
        ? {
            ...session,
            sharing_link_result: {
              ...session.sharing_link_result,
              answered_questions: session.sharing_link_result.answered_questions.map(answer =>
                answer.id === answerId
                  ? {
                      ...answer,
                      note,
                    }
                  : answer
              ),
              disabledSave: false,
            },
          }
        : session
    );
  }

  @action.bound
  public putFlashMessages(data: MessageProps) {
    const { appStore } = this.rootStore;
    appStore.handleFlashMessage(data);
  }

  @action.bound
  public async getClientList() {
    const {
      appStore: { organizationId: organization_id },
    } = this.rootStore;
    try {
      const { data } = await this.clientApi.getClients({ organization_id });
      this.clientList = data.clients;
    } catch (error) {
      this.error = error;
    }
  }

  @action.bound
  public async getHearingSetList() {
    const {
      appStore: { organizationId: organization_id },
    } = this.rootStore;
    this.loadingHearingList = true;
    try {
      const { data } = await this.hearingSetApi.getHearingSets(organization_id);
      this.hearingListConst = data.hearing_sets;
      this.hearingList = data.hearing_sets;
    } catch (error) {
      this.error = error;
    } finally {
      this.loadingHearingList = false;
    }
  }

  @action.bound
  public handleSearchHearing(value: string) {
    this.hearingList = searchByTitle<HearingItem>(this.hearingListConst, value);
  }

  @action.bound
  public async handleCreateNewHearingLink(createHearingLinkData: CreateHearingLinkData) {
    const {
      appStore: { organizationId: organization_id },
    } = this.rootStore;
    try {
      this.submitting = true;
      this.loading = true;
      const { data } = await this.hearingLinkApi.createHearingLink(
        createHearingLinkData,
        organization_id
      );
      this.putFlashMessages({
        content: i18n.t('sharingLink.messages.createHearingLinkSuccess'),
        status: 'success',
      });
      History.push(`${routes.hearingLink}/${data.id}`);
    } catch (error) {
      this.putFlashMessages({
        content: i18n.t('sharingLink.messages.createHearingLinkFail'),
        status: 'error',
      });
    } finally {
      this.loading = false;
      this.submitting = false;
    }
  }

  @action.bound
  public async getHearingLinkList(
    page = 1,
    order = 'updated_at',
    sort = 'desc',
    user_id = undefined
  ) {
    const {
      appStore: { organizationId: organization_id },
    } = this.rootStore;
    try {
      this.loading = true;
      const { data } = await this.hearingLinkApi.getHearingLinks({
        page,
        order,
        sort,
        per_page: 20,
        user_id,
        organization_id,
      });

      this.hearingLinkList = data.hearing_links;
      this.linkListMeta = { ...this.linkListMeta, total: data.total, page, order, sort };
    } catch (error: any) {
      const content = error?.message ?? 'error';
      this.putFlashMessages({ content, status: 'error' });
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  public async getHearingLinkDetail(id: string) {
    const {
      appStore: { organizationId: organization_id },
    } = this.rootStore;
    try {
      this.loading = true;
      const { data } = await this.hearingLinkApi.getHearingLinkById(id, organization_id);
      this.hearingLinkDetail = { ...data, linkType: 'hearing' };
      this.hearingLinkDetail.client_name = data.client?.name || null;
      this.combinedPDFStatus = data.sharing_link_combined_pdf?.status;
      this.getHearingSetList();
      this.getClientList();
    } catch (error: any) {
      if (error?.message.includes('404')) {
        this.putFlashMessages({
          content: i18n.t('sharingLink.messages.notFoundHearingLink'),
          status: 'error',
        });
      }
      History.push(routes.hearingLink);
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  public async getHearingLinkResults(id: number) {
    const {
      appStore: { organizationId: organization_id },
    } = this.rootStore;
    try {
      const { data } = await this.hearingLinkApi.getHearingLinkResults(id, organization_id);
      this.hearingLinkResults = data.sessions;
    } catch (error) {
      this.error = error;
    }
  }

  // FIX ME: separate this function into 2 where so we don't have to pass optional boolean flags:
  //  - isDetail true
  //  - isDetail false

  @action.bound
  public async updateStatusHearingLink(id: number, status: string, isDetail?: boolean) {
    const {
      appStore: { organizationId: organization_id },
    } = this.rootStore;
    try {
      await this.hearingLinkApi.updateHearingLinkStatus({
        ids: [id],
        status,
        organization_id,
      });
      if (!isDetail) {
        if (status === hearingLinkStatus.archived) {
          const { page, sort, order } = this.linkListMeta;
          this.getHearingLinkList(page, order, sort);
        } else {
          this.hearingLinkList = this.hearingLinkList.map(it => {
            if (it.id === id) return { ...it, status };
            return it;
          });
        }
      } else {
        this.hearingLinkDetail = { ...this.hearingLinkDetail, status };
      }
      if (status === hearingLinkStatus.archived) {
        this.putFlashMessages({
          content: i18n.t('admin.common.messages.archiveSuccess', {
            item: i18n.t('common.actionTarget.links'),
          }),
          status: 'success',
        });
      } else {
        this.putFlashMessages({
          content: i18n.t('admin.productContent.messages.changeStatusSuccess'),
          status: 'success',
        });
      }
    } catch (error: any) {
      if (error?.message.includes('403')) {
        this.putFlashMessages({
          content:
            this.hearingLinkDetail.content_type === 'hearing_set'
              ? i18n.t('sharingLink.noPublicHearing')
              : this.hearingLinkDetail.content_type === 'product_contents'
              ? i18n.t('sharingLink.noPublicProductContent')
              : i18n.t('sharingLink.noPublicPlaylist'),
          status: 'error',
        });
        return;
      }
      const content = error?.message ?? 'error';
      this.putFlashMessages({ content, status: 'error' });
    }
  }

  @action.bound
  public async saveLinkInfo(id: string, option: LinkInfoOption) {
    const {
      appStore: { organizationId: organization_id },
    } = this.rootStore;
    try {
      const { data } = await this.hearingLinkApi.updateHearingLinkById({
        id,
        option,
        organization_id,
      });
      this.hearingLinkDetail = { ...this.hearingLinkDetail, ...data };
      this.putFlashMessages({
        content: i18n.t('sharingLink.messages.updateHearingLinkSuccess'),
        status: 'success',
      });
      this.combinedPDFStatus = data.sharing_link_combined_pdf?.status;
      this.setIsEditingHearingDetail(false);
    } catch (error) {
      this.setRegeneratingPDF(false);
      this.error = error;
      this.putFlashMessages({
        content: i18n.t('sharingLink.messages.updateHearingLinkFail'),
        status: 'error',
      });
    }
  }

  @action.bound
  public async saveViewSetting(id: string, viewSetting: ViewSettingOption) {
    const {
      appStore: { organizationId: organization_id },
    } = this.rootStore;
    try {
      const { data } = await this.hearingLinkApi.updateHearingLinkById({
        id,
        option: viewSetting,
        organization_id,
      });
      this.hearingLinkDetail = { ...this.hearingLinkDetail, ...data };

      this.putFlashMessages({
        content: i18n.t('sharingLink.messages.updateHearingLinkSuccess'),
        status: 'success',
      });
    } catch (error) {
      this.error = error;
      this.putFlashMessages({
        content: i18n.t('sharingLink.messages.updateHearingLinkFail'),
        status: 'error',
      });
    }
  }

  @action.bound
  public resetHearingLink() {
    this.hearingLinkDetail = { ...defaultHearingLink };
    this.setRegeneratingPDF(false);
    this.setIsEditingHearingDetail(false);
    this.hearingLinkResults = [];
    this.hearingList = this.hearingListConst;
    this.linkViewLogs = [];
  }

  @action.bound
  public resetContentList() {
    this.productContentList = [];
    this.hearingList = [];
    this.hearingListConst = [];
    this.clientList = [];
  }

  @action.bound
  public async getHearingLinkArchive(page = 1, order = 'updated_at', sort = 'desc') {
    this.loading = true;
    const {
      appStore: { organizationId: organization_id },
    } = this.rootStore;
    try {
      const { data } = await this.hearingLinkApi.getArchivedHearingLinks({
        page,
        order,
        sort,
        per_page: 20,
        organization_id,
      });

      this.hearingLinkArchives = data.hearing_links;
      this.linkListMeta = {
        ...this.linkListMeta,
        page: data.page,
        sort,
        order,
        total: data.total,
      };
    } catch (error) {
      this.error = error;
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  public async restoreHearingLink(id?: number, isDetail?: boolean) {
    const {
      appStore: { organizationId: organization_id },
    } = this.rootStore;
    try {
      const listId = id ? [id] : [];
      await this.hearingLinkApi.updateHearingLinkStatus({
        ids: listId,
        status: 'inactive',
        organization_id,
      });

      if (!isDetail) {
        const { page, sort, order } = this.linkListMeta;
        this.getHearingLinkArchive(page, order, sort);
      } else {
        if (id) {
          this.getHearingLinkDetail(id.toString());
        }
      }

      this.putFlashMessages({
        content: i18n.t('admin.common.messages.restoreSuccess', {
          item: i18n.t('common.actionTarget.links'),
        }),
        status: 'success',
      });
    } catch (error) {
      this.error = error;
    }
  }

  @action.bound
  public async submitSaveNote(sessionId: string) {
    const {
      appStore: { organizationId: organization_id },
    } = this.rootStore;
    try {
      const targetResult = this.hearingLinkResults.find(session => session.id === sessionId);

      if (!targetResult) return;
      const data: {
        id: number;
        note: string;
      }[] = targetResult.sharing_link_result.answered_questions.map(result => {
        return { id: result.id, note: trimSpace(result.note ?? '') };
      });

      await this.userAnswerApi.updateUserAnswerNote({ answered_questions: data }, organization_id);

      this.hearingLinkResults = this.hearingLinkResults.map(session =>
        session.sharing_link_result && session.id === sessionId
          ? {
              ...session,
              sharing_link_result: {
                ...session.sharing_link_result,
                disabledSave: true,
              },
            }
          : session
      );

      this.putFlashMessages({
        content: i18n.t('sharingLink.messages.updateHearingLinkSuccess'),
        status: 'success',
      });
    } catch (error) {
      this.putFlashMessages({
        content: i18n.t('sharingLink.messages.updateHearingLinkFail'),
        status: 'error',
      });
    }
  }

  @action.bound
  public async getProductContentList({ q = '', types = [], tags = [] }: ProductListParams) {
    const {
      appStore: { organizationId: organization_id },
    } = this.rootStore;
    this.loadingFileList = true;
    try {
      const { data } = await this.productContentApi.getProductContents(organization_id, {
        page: 1,
        per_page: q.length > 0 || tags.length > 0 ? undefined : 50,
        q,
        types,
        tags,
        sort: 'desc',
      });
      this.productContentList = data.product_contents;
    } catch (error: any) {
      const content = error?.message ?? 'error!';
      this.putFlashMessages({ content, status: 'error' });
    } finally {
      this.loadingFileList = false;
    }
  }

  @action.bound
  public async getPlaylistList(key: string) {
    const {
      appStore: { organizationId: organization_id },
    } = this.rootStore;
    this.loadingPlaylistList = true;
    try {
      const { data } = await this.playlistApi.getPublishedPlaylists({
        page: 1,
        per_page: 50,
        order: 'updated_at',
        sort: 'desc',
        organization_id,
        key,
      });
      this.playlistList = data.content_playlists;
    } catch (error: any) {
      this.error = error;
    } finally {
      this.loadingPlaylistList = false;
    }
  }

  @action.bound
  public async getViewingLogsById(id: number) {
    const {
      appStore: { organizationId: organization_id },
    } = this.rootStore;
    try {
      const { data } = await this.hearingLinkApi.getViewingLogs({
        id,
        organization_id,
      });
      this.linkViewLogs = data.viewing_logs;
    } catch (error: any) {
      this.error = error;
      this.putFlashMessages({
        content: i18n.t('sharingLink.getViewingLogsError'),
        status: 'error',
      });
    }
  }
}

export default HearingLinkStore;
